import React, { useId } from 'react';

interface NoiseFilterProps {
  opacity?: number;
  baseFrequency?: number;
  blend?: 'overlay' | 'soft-light' | 'multiply' | 'screen';
}

/**
 * SVG-based noise/grain filter for cinematic backgrounds
 * Adds subtle texture to the Futuristic theme
 */
export const NoiseFilter: React.FC<NoiseFilterProps> = ({
  opacity = 0.03,
  baseFrequency = 0.7,
  blend = 'overlay',
}) => {
  const filterId = useId();

  return (
    <div className="fixed inset-0 pointer-events-none z-[1]" style={{ mixBlendMode: blend }}>
      <svg className="absolute inset-0 w-full h-full" style={{ opacity }}>
        <defs>
          <filter id={filterId}>
            <feTurbulence
              type="fractalNoise"
              baseFrequency={baseFrequency}
              numOctaves={4}
              stitchTiles="stitch"
              result="noise"
            />
            <feColorMatrix
              type="saturate"
              values="0"
              in="noise"
              result="monoNoise"
            />
          </filter>
        </defs>
        <rect width="100%" height="100%" filter={`url(#${filterId})`} />
      </svg>
    </div>
  );
};

/**
 * Animated grain effect with subtle movement
 */
export const AnimatedGrain: React.FC<{ intensity?: number }> = ({ intensity = 0.04 }) => {
  const filterId = useId();

  return (
    <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
      <svg className="absolute w-[200%] h-[200%] -left-1/2 -top-1/2 animate-grain" style={{ opacity: intensity }}>
        <defs>
          <filter id={filterId}>
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.8"
              numOctaves="4"
              seed="1"
              stitchTiles="stitch"
            />
            <feColorMatrix type="saturate" values="0" />
          </filter>
        </defs>
        <rect width="100%" height="100%" filter={`url(#${filterId})`} />
      </svg>
      
      <style>{`
        @keyframes grain {
          0%, 100% { transform: translate(0, 0); }
          10% { transform: translate(-1%, -1%); }
          20% { transform: translate(1%, 1%); }
          30% { transform: translate(-2%, 0%); }
          40% { transform: translate(0%, 2%); }
          50% { transform: translate(2%, -1%); }
          60% { transform: translate(-1%, 2%); }
          70% { transform: translate(1%, -2%); }
          80% { transform: translate(-2%, 1%); }
          90% { transform: translate(2%, 0%); }
        }
        .animate-grain {
          animation: grain 0.5s steps(10) infinite;
        }
      `}</style>
    </div>
  );
};

/**
 * Scanline effect for retro-futuristic aesthetic
 */
export const Scanlines: React.FC<{ opacity?: number; spacing?: number }> = ({ 
  opacity = 0.03, 
  spacing = 4 
}) => {
  return (
    <div 
      className="fixed inset-0 pointer-events-none z-[2]"
      style={{
        background: `repeating-linear-gradient(
          0deg,
          transparent,
          transparent ${spacing - 1}px,
          rgba(0, 0, 0, ${opacity}) ${spacing - 1}px,
          rgba(0, 0, 0, ${opacity}) ${spacing}px
        )`,
      }}
    />
  );
};

/**
 * CRT-style vignette for added depth
 */
export const Vignette: React.FC<{ intensity?: number }> = ({ intensity = 0.4 }) => {
  return (
    <div 
      className="fixed inset-0 pointer-events-none z-[3]"
      style={{
        background: `radial-gradient(
          ellipse at center,
          transparent 0%,
          transparent 50%,
          rgba(0, 0, 0, ${intensity * 0.3}) 80%,
          rgba(0, 0, 0, ${intensity}) 100%
        )`,
      }}
    />
  );
};
